// Modal de detalhe de uma notificação: mostra o "de -> para" estruturado da
// alteração (turma antiga x nova, status antigo x novo) que gerou a
// notificação, em vez de só o texto solto. Usado pelo sino (NotificationBell)
// e pela página cheia (Notificacoes.js) — mesmo componente, pra manter os
// dois lugares consistentes.
//
// `detalhes` só existe pra notificações criadas depois da migração
// migrate-add-notificacoes-detalhes.js — notificações mais antigas (ou avisos
// de sistema manuais, que não representam uma alteração de dado) caem no
// fallback que só mostra `mensagem`.
import React from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Modal, Button, Badge } from './ui';

function LadoCard({ lado, vazio }) {
  if (!lado) {
    return (
      <div className="flex-1 min-w-0 rounded-xl border border-dashed border-gray-200 dark:border-gray-700 p-2.5 flex items-center justify-center">
        <p className="text-xs text-gray-400 dark:text-gray-500 italic text-center">{vazio}</p>
      </div>
    );
  }
  if (lado.status) {
    const ativo = lado.status === 'ativo';
    return (
      <div className="flex-1 min-w-0 rounded-xl border border-gray-200 dark:border-gray-700 p-2.5 flex items-center justify-center">
        <Badge tone={ativo ? 'success' : 'danger'}>{ativo ? 'Ativo' : 'Inativo'}</Badge>
      </div>
    );
  }
  return (
    <div className="flex-1 min-w-0 rounded-xl border border-gray-200 dark:border-gray-700 p-2.5">
      <p className="text-xs font-semibold text-gray-900 dark:text-gray-100 truncate" translate="no">
        {lado.turma}
      </p>
      <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
        {lado.dia_semana} {lado.horario} · {lado.turno}
      </p>
      {lado.professor && (
        <p className="text-[11px] text-gray-400 dark:text-gray-500 truncate" translate="no">
          Prof. {lado.professor}
        </p>
      )}
    </div>
  );
}

function LinhaAlteracao({ item }) {
  return (
    <div className="space-y-1.5">
      {item.aluno_nome && (
        <p className="text-xs font-medium text-gray-700 dark:text-gray-300" translate="no">
          {item.aluno_nome}
        </p>
      )}
      <div className="flex items-center gap-2">
        <LadoCard lado={item.de} vazio="Nova matrícula" />
        <ArrowRight className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
        <LadoCard lado={item.para} vazio="Removido da grade" />
      </div>
    </div>
  );
}

export default function NotificacaoDetalheModal({ notificacao, onClose }) {
  const navigate = useNavigate();
  if (!notificacao) return null;
  const detalhes = Array.isArray(notificacao.detalhes) ? notificacao.detalhes : [];

  const irParaAluno = () => {
    onClose();
    navigate(`/gerenciar-matriculas/aluno/${notificacao.id_aluno}/editar`);
  };
  const irParaAjusteGrade = () => {
    onClose();
    navigate('/ajuste-grade');
  };

  // Portal pro <body>: quem chama esse componente pode estar dentro do
  // <header> (ver NotificationBell.jsx), que tem transform-gpu — isso vira o
  // containing block de qualquer `position: fixed` dentro dele (mesmo motivo
  // pelo qual o dropdown do sino usa um listener no lugar de overlay fixed).
  // Sem o portal, o Modal (fixed inset-0) ficaria preso na faixa de 64px do
  // header em vez de cobrir a tela.
  return createPortal(
    <Modal isOpen={!!notificacao} onClose={onClose} title={notificacao.titulo} maxWidth="md">
      <div className="space-y-4">
        {detalhes.length > 0 ? (
          <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-1">
            {detalhes.map((item, i) => (
              <LinhaAlteracao key={i} item={item} />
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400" translate="no">
            {notificacao.mensagem || 'Sem detalhes adicionais pra essa notificação.'}
          </p>
        )}

        {(notificacao.id_aluno || notificacao.tipo === 'movimentacao') && (
          <div className="flex justify-end gap-2 pt-1 border-t border-gray-100 dark:border-gray-800">
            {notificacao.id_aluno ? (
              <Button variant="secondary" onClick={irParaAluno}>
                Abrir cadastro do aluno
              </Button>
            ) : (
              <Button variant="secondary" onClick={irParaAjusteGrade}>
                Abrir Ajuste de Grade
              </Button>
            )}
          </div>
        )}
      </div>
    </Modal>,
    document.body,
  );
}
